import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Cookies from 'universal-cookie';
import { loginService, sendLoginOtpService, userProfileService, verifyLoginOtpService } from '../../../api/services/userServices';
import { Menu, X, Mail, Lock, Facebook, Twitter, Instagram, Linkedin, Phone, MapPin } from 'lucide-react';
import { useUserContext } from '../../protectedRoute/UserContext';
import { motion } from "framer-motion";
import { Button } from "../../../components/ui/button";
import { useForm } from "react-hook-form";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "../../../components/ui/form";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const passwordFormSchema = z.object({
    phone_number: z.string().min(10, { message: "Enter a valid 10-digit mobile number." }).max(15),
    password: z.string().min(6, { message: "Password must be at least 6 characters." }),
    rememberMe: z.boolean().default(false).optional(),
});

const phoneFormSchema = z.object({
    phone_number: z.string().min(10, { message: "Enter a valid 10-digit mobile number." }).max(15),
});

const otpFormSchema = z.object({
    otp: z.string().length(6, { message: "OTP must be 6 digits." }),
});

type LoginTab = 'password' | 'otp';
type OtpStep = 'phone' | 'otp';

const Login: React.FC = () => {
    const { state, dispatch } = useUserContext();
    const { role } = state;
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<LoginTab>('password');
    const [otpStep, setOtpStep] = useState<OtpStep>('phone');
    const [otpPhone, setOtpPhone] = useState('');
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const cookies = new Cookies();

    const passwordForm = useForm<z.infer<typeof passwordFormSchema>>({
        resolver: zodResolver(passwordFormSchema),
        defaultValues: { phone_number: "", password: "", rememberMe: false },
    });

    const phoneForm = useForm<z.infer<typeof phoneFormSchema>>({
        resolver: zodResolver(phoneFormSchema),
        defaultValues: { phone_number: "" },
    });

    const otpForm = useForm<z.infer<typeof otpFormSchema>>({
        resolver: zodResolver(otpFormSchema),
        defaultValues: { otp: "" },
    });

    useEffect(() => {
        if (role) {
            const redirectPath = new URLSearchParams(location.search).get('redirect');
            const queryString = location.search.split('redirect=')[1]
                ? location.search.split('redirect=')[1].split('&').slice(1).join('&')
                : '';
            if (redirectPath) {
                navigate(`${redirectPath}?${queryString}`, { replace: true });
            } else {
                switch (role) {
                    case 'guest': navigate('/guest-search-results', { replace: true }); break;
                    case 'admin': navigate('/admin/dashboard', { replace: true }); break;
                    // 'host' intentionally omitted — host access moved to Occupi app
                    default: navigate('/', { replace: true });
                }
            }
        }
    }, [role, navigate, location.search]);

    const handleLoginSuccess = async (response: any, identifier: string) => {
        toast.success(`Welcome, ${identifier}!`);
        cookies.set('token', response.token, { expires: new Date(Date.now() + 24 * 60 * 60 * 1000) });
        if (response.organization_id) {
            cookies.set('organization_id', response.organization_id, { expires: new Date(Date.now() + 24 * 60 * 60 * 1000) });
        }
        if (response.organization_role) {
            cookies.set('organization_role', response.organization_role, { expires: new Date(Date.now() + 24 * 60 * 60 * 1000) });
        }
        dispatch({ type: 'SET_ROLE', payload: response.role });
        dispatch({ type: 'SET_HOST_ID', payload: response.id });
        if (response.organization_id) dispatch({ type: 'SET_ORGANIZATION_ID', payload: response.organization_id });
        if (response.organization_role) dispatch({ type: 'SET_ORGANIZATION_ROLE', payload: response.organization_role });
        if (response.organization_is_verified) dispatch({ type: 'SET_ORGANIZATION_VERIFIED', payload: response.organization_is_verified });

        const userProfile = await userProfileService();
        if (userProfile) {
            dispatch({ type: 'SET_USER', payload: { first_name: userProfile.first_name ?? '', last_name: userProfile.last_name ?? '', email: userProfile.email ?? '', phone_number: userProfile.phone_number ?? '' } });
        }
    };

    const onPasswordSubmit = async (values: z.infer<typeof passwordFormSchema>) => {
        try {
            const response = await loginService({ phone_number: values.phone_number, password: values.password });
            if (response?.role === 'host') {
                toast.error('Host accounts are not registered here. Please use the Occupi app.');
                return;
            }
            if (response?.role) {
                await handleLoginSuccess(response, values.phone_number);
                passwordForm.reset();
            } else {
                toast.error('Invalid login credentials.');
            }
        } catch {
            toast.error('An error occurred. Please try again later.');
        }
    };

    const onSendOtp = async (values: z.infer<typeof phoneFormSchema>) => {
        setIsSendingOtp(true);
        try {
            const response = await sendLoginOtpService(values.phone_number);
            if (response?.message) {
                const devOtp = (response as any)?.dev_otp;
                if (devOtp) {
                    toast.info(`[DEV] OTP: ${devOtp}`, { autoClose: 30000 });
                } else {
                    toast.success('OTP sent to your mobile number.');
                }
                setOtpPhone(values.phone_number);
                setOtpStep('otp');
            } else {
                toast.error(response?.error || 'Could not send OTP. Check your mobile number.');
            }
        } catch {
            toast.error('An error occurred. Please try again.');
        } finally {
            setIsSendingOtp(false);
        }
    };

    const onVerifyOtp = async (values: z.infer<typeof otpFormSchema>) => {
        setIsVerifyingOtp(true);
        try {
            const response = await verifyLoginOtpService(otpPhone, values.otp);
            if (response?.role === 'host') {
                toast.error('Host accounts are not registered here. Please use the Occupi app.');
                otpForm.reset();
                phoneForm.reset();
                setOtpStep('phone');
                return;
            }
            if (response?.role) {
                await handleLoginSuccess(response, otpPhone);
                otpForm.reset();
                phoneForm.reset();
                setOtpStep('phone');
            } else {
                toast.error('Invalid or expired OTP. Please try again.');
            }
        } catch {
            toast.error('An error occurred. Please try again.');
        } finally {
            setIsVerifyingOtp(false);
        }
    };

    const handleResendOtp = async () => {
        setIsSendingOtp(true);
        try {
            const response = await sendLoginOtpService(otpPhone);
            if (response?.message) {
                toast.success('OTP resent successfully.');
            } else {
                toast.error(response?.error || 'Could not resend OTP.');
            }
        } catch {
            toast.error('An error occurred.');
        } finally {
            setIsSendingOtp(false);
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen flex flex-col">
            {/* Header */}
            <motion.header
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
            >
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-16 md:h-20">
                        <motion.div className="flex items-center gap-2" whileHover={{ scale: 1.02 }}>
                            <Link to="/" className="text-2xl md:text-3xl font-bold">
                                <span className="text-primary">Social</span>
                                <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
                            </Link>
                        </motion.div>

                        <nav className="hidden md:flex items-center gap-8">
                            {["Home", "About", "Contact"].map((item) => (
                                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`} className="text-foreground/80 hover:text-primary font-medium transition-colors">
                                    {item}
                                </Link>
                            ))}
                        </nav>

                        <div className="hidden md:flex items-center gap-4">
                            <Link to="/login"><Button variant="ghost" className="text-foreground hover:text-primary">Login</Button></Link>
                            <Link to="/signup"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">Register</Button></Link>
                        </div>

                        <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>

                    {isMobileMenuOpen && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="md:hidden py-4 border-t border-border">
                            <nav className="flex flex-col gap-4">
                                {["Home", "About", "Contact"].map((item) => (
                                    <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`} className="text-foreground/80 hover:text-primary font-medium transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                                        {item}
                                    </Link>
                                ))}
                                <div className="flex flex-col gap-2 pt-4 border-t border-border">
                                    <Link to="/login"><Button variant="ghost" className="justify-start">Login</Button></Link>
                                    <Link to="/signup"><Button className="bg-primary text-primary-foreground">Register</Button></Link>
                                </div>
                            </nav>
                        </motion.div>
                    )}
                </div>
            </motion.header>

            <main className="flex-1 flex items-center justify-center pt-20 md:pt-24 pb-16 px-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md bg-card rounded-2xl shadow-lg p-8"
                >
                    <h1 className="text-2xl md:text-3xl font-bold text-primary text-center mb-2">Welcome Back!</h1>
                    <p className="text-foreground/70 text-center mb-6">Login to continue your journey with Social Bunkr.</p>

                    {/* Tab Toggle */}
                    <div className="flex rounded-lg border border-input overflow-hidden mb-6">
                        <button
                            type="button"
                            onClick={() => { setActiveTab('password'); setOtpStep('phone'); }}
                            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${activeTab === 'password' ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
                        >
                            Phone & Password
                        </button>
                        <button
                            type="button"
                            onClick={() => { setActiveTab('otp'); setOtpStep('phone'); }}
                            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${activeTab === 'otp' ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
                        >
                            OTP Login
                        </button>
                    </div>

                    {/* Password Login */}
                    {activeTab === 'password' && (
                        <Form {...passwordForm}>
                            <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="phone_number">Mobile Number</Label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <FormField
                                            control={passwordForm.control}
                                            name="phone_number"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Input id="phone_number" type="tel" placeholder="Enter your 10-digit mobile number" className="pl-10" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <FormField
                                            control={passwordForm.control}
                                            name="password"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Input id="password" type="password" placeholder="••••••••" className="pl-10" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <FormField
                                        control={passwordForm.control}
                                        name="rememberMe"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border-0 p-0">
                                                <FormControl>
                                                    <Checkbox id="rememberMe" checked={field.value} onCheckedChange={field.onChange} />
                                                </FormControl>
                                                <div className="space-y-1 leading-none">
                                                    <Label htmlFor="rememberMe" className="text-sm cursor-pointer">Remember me</Label>
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                    <Link to="/forgot-password" className="text-sm text-primary hover:underline">Forgot password?</Link>
                                </div>

                                <Button type="submit" className="w-full bg-primary hover:bg-primary/90">Login</Button>
                            </form>
                        </Form>
                    )}

                    {/* OTP Login */}
                    {activeTab === 'otp' && otpStep === 'phone' && (
                        <Form {...phoneForm}>
                            <form onSubmit={phoneForm.handleSubmit(onSendOtp)} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="phone_number">Mobile Number</Label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <FormField
                                            control={phoneForm.control}
                                            name="phone_number"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Input id="phone_number" type="tel" placeholder="Enter your registered mobile number" className="pl-10" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={isSendingOtp}>
                                    {isSendingOtp ? 'Sending OTP...' : 'Send OTP'}
                                </Button>
                            </form>
                        </Form>
                    )}

                    {activeTab === 'otp' && otpStep === 'otp' && (
                        <div className="space-y-5">
                            <p className="text-sm text-foreground/70 text-center">
                                OTP sent to <span className="font-medium text-foreground">{otpPhone}</span>.{' '}
                                <button type="button" onClick={() => setOtpStep('phone')} className="text-primary hover:underline">Change</button>
                            </p>
                            <Form {...otpForm}>
                                <form onSubmit={otpForm.handleSubmit(onVerifyOtp)} className="space-y-5">
                                    <div className="space-y-2">
                                        <Label htmlFor="otp">Enter OTP</Label>
                                        <FormField
                                            control={otpForm.control}
                                            name="otp"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Input
                                                            id="otp"
                                                            type="text"
                                                            inputMode="numeric"
                                                            maxLength={6}
                                                            placeholder="6-digit OTP"
                                                            className="text-center tracking-widest text-lg"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={isVerifyingOtp}>
                                        {isVerifyingOtp ? 'Verifying...' : 'Verify & Login'}
                                    </Button>
                                </form>
                            </Form>
                            <p className="text-center text-sm text-foreground/60">
                                Didn't receive OTP?{' '}
                                <button type="button" onClick={handleResendOtp} disabled={isSendingOtp} className="text-primary hover:underline disabled:opacity-50">
                                    {isSendingOtp ? 'Resending...' : 'Resend OTP'}
                                </button>
                            </p>
                        </div>
                    )}

                    <p className="text-center text-foreground/70 mt-6">
                        Don't have an account?{" "}
                        <Link to="/signup" className="text-primary font-medium hover:underline">Sign up here</Link>
                    </p>
                </motion.div>
            </main>

            {/* Footer */}
            <footer className="bg-foreground text-background py-16">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
                        <div>
                            <div className="mb-6">
                                <Link to="/" className="text-2xl font-bold">
                                    <span className="text-primary-foreground">Social</span>
                                    <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
                                </Link>
                            </div>
                            <p className="text-background/70 mb-6 leading-relaxed">
                                Bharat's most affordable stay platform. Experience comfortable living with all-inclusive amenities.
                            </p>
                            <div className="flex gap-4">
                                <motion.a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer" whileHover={{ y: -3, scale: 1.1 }} className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-colors">
                                    <Facebook size={18} />
                                </motion.a>
                                <motion.a href="https://twitter.com/" target="_blank" rel="noopener noreferrer" whileHover={{ y: -3, scale: 1.1 }} className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-colors">
                                    <Twitter size={18} />
                                </motion.a>
                                <motion.a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" whileHover={{ y: -3, scale: 1.1 }} className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-colors">
                                    <Instagram size={18} />
                                </motion.a>
                                <motion.a href="#" whileHover={{ y: -3, scale: 1.1 }} className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-colors">
                                    <Linkedin size={18} />
                                </motion.a>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
                            <ul className="space-y-3">
                                <li><Link to="/about" className="text-background/70 hover:text-secondary transition-colors">About Us</Link></li>
                                <li><Link to="#" className="text-background/70 hover:text-secondary transition-colors">How it Works</Link></li>
                                <li><Link to="#" className="text-background/70 hover:text-secondary transition-colors">Destinations</Link></li>
                                <li><Link to="#" className="text-background/70 hover:text-secondary transition-colors">Blog</Link></li>
                                <li><Link to="#" className="text-background/70 hover:text-secondary transition-colors">Careers</Link></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-lg font-semibold mb-6">Support</h4>
                            <ul className="space-y-3">
                                <li><Link to="#" className="text-background/70 hover:text-secondary transition-colors">Help Center</Link></li>
                                <li><Link to="#" className="text-background/70 hover:text-secondary transition-colors">FAQs</Link></li>
                                <li><Link to="#" className="text-background/70 hover:text-secondary transition-colors">Terms of Service</Link></li>
                                <li><Link to="#" className="text-background/70 hover:text-secondary transition-colors">Privacy Policy</Link></li>
                                <li><Link to="#" className="text-background/70 hover:text-secondary transition-colors">Refund Policy</Link></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3">
                                    <MapPin size={20} className="text-secondary flex-shrink-0 mt-1" />
                                    <span className="text-background/70">Brundavan colony, Plot no 105, Kokapet, Hyderabad, Telangana 500075</span>
                                </li>
                                <li className="flex items-center gap-3">
                                    <Phone size={20} className="text-secondary flex-shrink-0" />
                                    <span className="text-background/70">+91-8008585553</span>
                                </li>
                                <li className="flex items-center gap-3">
                                    <Mail size={20} className="text-secondary flex-shrink-0" />
                                    <span className="text-background/70">hello@socialbunkr.in</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-background/20 pt-8">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                            <p className="text-background/60 text-sm">© {new Date().getFullYear()} Social Bunkr. All rights reserved.</p>
                            <div className="flex gap-6">
                                <Link to="#" className="text-background/60 hover:text-secondary text-sm transition-colors">Terms</Link>
                                <Link to="#" className="text-background/60 hover:text-secondary text-sm transition-colors">Privacy</Link>
                                <Link to="#" className="text-background/60 hover:text-secondary text-sm transition-colors">Cookies</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
            <ToastContainer />
        </div>
    );
};

export default Login;
